import { useMutation, useQueryClient } from "react-query";
import { fetchData } from "../api/client";
import { uploadOne } from "./useS3Upload";
import { INTERNAL_NOTE_CHANNEL_ID } from "../components/MessageHistory/ChannelSelector";
import { stripOperatorNamePrefix } from "../utils/operatorNamePrefix";
import { workspaceScopedQueryKey } from "../utils/workspaceQueryScope";

export const useMessages = () => {
  const queryClient = useQueryClient();

  const sendMessageMutation = useMutation(
    async ({
      conversationId,
      message,
      file,
      audioURL,
      channel,
      projectId,
      userId,
      userName,
      phone,
      email,
      emailTo,
      emailSubject,
      emailCc,
      files,
      templateId,
      metaMessagingTag,
      location,
      locationName,
      locationAddress,
      contact,
      buttonMessage,
      mentions,
      mentionNames,
      mentionAll,
      type: requestedType,
      pollOptions,
      selectableCount,
      transport,
      quotedMessageId,
      quotedParticipant,
    }) => {
      if (!conversationId) throw new Error("ID da conversa não informado.");

      const isLocation =
        location &&
        typeof location === "object" &&
        location.latitude != null &&
        location.longitude != null;

      const isContact =
        contact &&
        typeof contact === "object" &&
        String(contact.name || contact.nome || "").trim() &&
        String(contact.phone || contact.telefone || "").trim();

      const isInternalNote = String(channel) === String(INTERNAL_NOTE_CHANNEL_ID);
      let outboundContent = message?.trim() || "";
      if (isInternalNote && outboundContent) {
        outboundContent = stripOperatorNamePrefix(outboundContent, [
          userName,
        ]);
      }

      const messagePayload = {
        conversationId,
        type:
          requestedType === "poll" || requestedType === "sticker"
            ? requestedType
            : file
              ? file.type === "image/webp" && requestedType === "sticker"
                ? "sticker"
                : "file"
              : audioURL
                ? "audio"
                : isLocation
                  ? "location"
                  : isContact
                    ? "contact"
                    : "text",
        content: outboundContent,
        channel,
        ...(transport === "webchat" || transport === "whatsapp" ? { transport } : {}),
        projectId,
        userId,
        phone,
        email: emailTo ?? email,
        ...(emailTo != null && { emailTo }),
        ...(emailSubject != null && { emailSubject }),
        ...(emailCc != null && { emailCc }),
        ...(templateId != null && { templateId }),
        ...(metaMessagingTag != null && { metaMessagingTag }),
        ...(buttonMessage != null && String(buttonMessage).trim()
          ? { buttonMessage: String(buttonMessage).trim() }
          : {}),
        ...(Array.isArray(mentions) && mentions.length ? { mentions } : {}),
        ...(mentionNames && typeof mentionNames === "object" && !Array.isArray(mentionNames)
          ? { mentionNames }
          : {}),
        ...(mentionAll === true ? { mentionAll: true } : {}),
        ...(requestedType === "poll" && Array.isArray(pollOptions) ? { pollOptions, selectableCount } : {}),
        ...(quotedMessageId ? { quotedMessageId } : {}),
        ...(quotedParticipant ? { quotedParticipant } : {}),
        ...(isLocation && {
          latitude: location.latitude,
          longitude: location.longitude,
          ...(locationName != null && String(locationName).trim() ? { locationName: String(locationName).trim() } : {}),
          ...(locationAddress != null && String(locationAddress).trim()
            ? { locationAddress: String(locationAddress).trim() }
            : {}),
        }),
        ...(isContact && {
          contact: {
            name: String(contact.name || contact.nome || "").trim(),
            phone: String(contact.phone || contact.telefone || "").trim(),
            ...(contact.email != null && String(contact.email).trim()
              ? { email: String(contact.email).trim() }
              : {}),
            ...(contact.company != null && String(contact.company).trim()
              ? { company: String(contact.company).trim() }
              : {}),
          },
        }),
      };

      // console.log("✅ sendMessageMutation Payload (Antes de conversão):", messagePayload);

      // 🔹 Upload dos anexos do e-mail: um único envio leva todos os arquivos.
      const emailFiles = Array.isArray(files) ? files.filter(Boolean) : [];
      if (emailFiles.length) {
        const uploadedFiles = [];
        for (const emailFile of emailFiles) {
          try {
            const uploaded = await uploadOne(emailFile, { prefix: "chat" });
            uploadedFiles.push({
              url: uploaded.url,
              key: uploaded.key,
              name: emailFile.name,
              type: emailFile.type,
              size: emailFile.size,
            });
          } catch {
            throw new Error(
              "Não foi possível enviar o e-mail com o anexo. Nada foi enviado. Tente de novo ou use um arquivo menor."
            );
          }
        }
        messagePayload.files = uploadedFiles;
        messagePayload.type = "text";
      } else if (file) {
        const uploaded = await uploadOne(file, { prefix: "chat" });
        messagePayload.file = {
          url: uploaded.url,
          key: uploaded.key,
          name: file.name,
          type: file.type,
          size: file.size,
        };
      }

      // 🔹 Faz upload do áudio gravado para o S3 (mic-recorder-to-mp3 gera MP3)
      // Gupshup exige audio/mpeg (não audio/mp3) para áudio
      if (audioURL) {
        const audioBlob = await fetchBlobFromUrl(audioURL);
        const mimeType = audioBlob.type && audioBlob.type !== "audio/mp3" ? audioBlob.type : "audio/mpeg";
        const fileName = `audio-${Date.now()}.mp3`;
        const audioFile = new File([audioBlob], fileName, { type: mimeType });
        const uploaded = await uploadOne(audioFile, { prefix: "chat" });
        messagePayload.audio = uploaded.url;
      }

      // console.log("✅ Payload Final com Base64:", messagePayload);

      const res = await fetchData("/messages", "POST", messagePayload);
      return {
        ...res,
        __sentFile: messagePayload.file || null,
        __sentFiles: messagePayload.files || null,
      };
    },
    {
      onSuccess: (_, { conversationId }) => {
        queryClient.invalidateQueries(
          workspaceScopedQueryKey("conversationDetails", conversationId)
        );
      },
      onError: (error) => {
        console.error("❌ Erro ao enviar mensagem:", error);
      },
    }
  );

  return { sendMessageMutation };
};

/**
 * 🔹 Obtém um Blob a partir de uma URL (usado para enviar o áudio gravado)
 */
const fetchBlobFromUrl = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error("Falha ao preparar arquivo de áudio para envio.");
  }
  return await response.blob();
};
